import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { BottomTabBar } from '@/components/layout/BottomTabBar'
import { apiUrl } from '@/lib/api'

interface Student {
  id: string
  studentNumber: number
  prefix: string
  fullName: string
  nickname: string | null
  lineLinked: boolean
}

interface StudentsResponse {
  success: boolean
  data: {
    students: Student[]
    total: number
    linked: number
  }
}

export function StudentListPage() {
  const navigate = useNavigate()
  const { teacher } = useAuth()
  const [students, setStudents] = useState<Student[]>([])
  const [total, setTotal] = useState(0)
  const [linked, setLinked] = useState(0)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchStudents()
  }, [])

  async function fetchStudents() {
    setIsLoading(true)
    try {
      const res = await fetch(apiUrl('/api/students'), { credentials: 'include' })
      const data: StudentsResponse = await res.json()
      if (data.success) {
        setStudents(data.data.students)
        setTotal(data.data.total)
        setLinked(data.data.linked)
      }
    } catch {
      // TODO: error toast
    } finally {
      setIsLoading(false)
    }
  }

  // Client-side search with debounce effect
  const filtered = useMemo(() => {
    if (!search.trim()) return students
    const q = search.trim().toLowerCase()
    return students.filter(
      (s) =>
        s.fullName.toLowerCase().includes(q) ||
        (s.nickname && s.nickname.toLowerCase().includes(q)) ||
        String(s.studentNumber).includes(q),
    )
  }, [students, search])

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">
            นักเรียน {teacher?.classroomId === 'cls-001' ? 'ป.3/4' : ''}
          </h1>
          <button
            onClick={() => navigate('/students/import')}
            className="text-sm font-medium text-primary-600"
          >
            นำเข้า
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-3">
        {/* Search */}
        <div className="relative mb-3">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <SearchIcon />
          </span>
          <input
            type="text"
            placeholder="ค้นหาชื่อหรือชื่อเล่น..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {/* Summary */}
        <p className="mb-3 text-sm text-gray-500">
          ทั้งหมด {total} คน · LINE linked {linked} คน
        </p>

        {/* Student list */}
        {isLoading ? (
          <SkeletonList />
        ) : filtered.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400 shadow-sm">
            {search ? 'ไม่พบนักเรียนที่ค้นหา' : 'ยังไม่มีนักเรียน'}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg bg-white shadow-sm">
            {/* Table header */}
            <div className="grid grid-cols-[3rem_1fr_3rem_2rem] border-b border-gray-100 px-3 py-2 text-xs font-medium text-gray-500">
              <span>#</span>
              <span>ชื่อ</span>
              <span className="text-center">LINE</span>
              <span />
            </div>

            {/* Rows */}
            {filtered.map((student) => (
              <button
                key={student.id}
                onClick={() => navigate(`/students/${student.id}`)}
                className="grid w-full grid-cols-[3rem_1fr_3rem_2rem] items-center border-b border-gray-50 px-3 py-3 text-left text-sm transition-colors last:border-0 hover:bg-gray-50 active:bg-gray-100"
              >
                <span className="font-medium text-gray-600">{student.studentNumber}</span>
                <span className="truncate text-gray-900">
                  {student.prefix}
                  {student.fullName}
                  {student.nickname && (
                    <span className="ml-1 text-gray-400">({student.nickname})</span>
                  )}
                </span>
                <span className="text-center">
                  {student.lineLinked ? (
                    <span className="text-green-500" title="LINE linked">
                      ✓
                    </span>
                  ) : (
                    <span className="text-orange-400" title="ยังไม่ได้ link">
                      !
                    </span>
                  )}
                </span>
                <span className="text-gray-300">▸</span>
              </button>
            ))}
          </div>
        )}

        {/* Add button */}
        <button
          onClick={() => navigate('/students/new')}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 active:bg-primary-800"
        >
          + เพิ่มนักเรียน
        </button>
      </main>

      <BottomTabBar active="students" />
    </div>
  )
}

function SearchIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  )
}

function SkeletonList() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex animate-pulse items-center gap-3 rounded-lg bg-white p-3 shadow-sm">
          <div className="h-4 w-8 rounded bg-gray-200" />
          <div className="h-4 flex-1 rounded bg-gray-200" />
          <div className="h-4 w-6 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  )
}
